import { Button, Card, Progress, buttonVariants, cn } from "@autonoma/blacklight";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { PlusIcon } from "@phosphor-icons/react/Plus";
import { QuestionIcon } from "@phosphor-icons/react/Question";
import { ShieldCheckIcon } from "@phosphor-icons/react/ShieldCheck";
import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { Link, createFileRoute, redirect, useRouteContext } from "@tanstack/react-router";
import { SUPPORT_URL } from "components/talk-to-support";
import { env } from "env";
import { useAuth } from "lib/auth";
import { isMidOnboarding } from "lib/onboarding/app-onboarding";
import { buildResumeSearch } from "lib/onboarding/navigate-to-onboarding";
import { getOnboardingProgress } from "lib/onboarding/onboarding-progress";
import { buildOnboardingSearch } from "lib/onboarding/onboarding-search";
import { ensureShellNavState } from "lib/query/app-shell.queries";
import { type ReactNode, useState } from "react";
import { getLastAppId } from "./-last-app";

const APP_TYPE_LABELS: Record<string, string> = {
  WEB: "Web application",
  IOS: "iOS application",
  ANDROID: "Android application",
};

// Only collapse a section once it grows past this many apps; smaller sections
// stay fully expanded so a short list is never hidden behind a chevron.
const COLLAPSE_THRESHOLD = 5;

interface AppCardData {
  id: string;
  name: string;
  slug: string;
  architecture: string;
  githubRepositoryId?: number | null;
  onboardingState?: { step: string } | null;
}

function appTypeLabel(architecture: string): string {
  return APP_TYPE_LABELS[architecture] ?? "Application";
}

export const Route = createFileRoute("/_blacklight/_app-shell/")({
  beforeLoad: async ({ context }) => {
    const onboardedApps = context.applications.filter((app) => !isMidOnboarding(app));
    const incompleteApps = context.applications.filter(isMidOnboarding);

    // Only land on the hub when the user has nothing usable yet but does have a setup
    // in flight - so they can resume it. As soon as a single app is fully configured we
    // deep-link straight into it instead of showing this picker.
    const shouldShowHub = onboardedApps.length === 0 && incompleteApps.length > 0;
    if (shouldShowHub) return;

    // No configured apps and nothing in progress: handled one level up (the app-shell
    // route redirects to onboarding when there are no applications at all).
    if (onboardedApps.length === 0) return;

    // At least one app is ready - deep-link into it. Prefer the last viewed, otherwise the first.
    const lastAppId = getLastAppId();
    const targetApp = onboardedApps.find((a) => a.id === lastAppId) ?? onboardedApps[0];
    if (targetApp == null) return;

    // "Onboarded" here is answered from `step` alone, which the app list carries - and an app can
    // have gone live while its setup steps are still outstanding. The app route gates on exactly
    // that and would bounce this deep-link into onboarding, and because the hub only renders when
    // NOTHING is onboarded, it would never get a chance to draw: an account whose last-viewed app
    // is unfinished could not reach its other, working apps at all.
    //
    // One read, for the single app we are about to jump into rather than for the list, and only on
    // the way through the hub. Unconfirmed falls through to rendering the picker, which is the safe
    // direction - the worst case is showing a chooser to someone who would have been deep-linked.
    const navState = await ensureShellNavState(context.queryClient, targetApp.id).catch(() => undefined);
    if (navState?.setupComplete === false) return;

    throw redirect({
      to: "/app/$appSlug",
      params: { appSlug: targetApp.slug },
      replace: true,
    });
  },
  component: AppSelector,
});

function AppSelector() {
  const applications = useRouteContext({ from: "/_blacklight/_app-shell", select: (ctx) => ctx.applications });
  const { user } = useAuth();
  const incompleteApps = applications.filter(isMidOnboarding);
  const completedApps = applications.filter((app) => !isMidOnboarding(app));
  const hasCompleted = completedApps.length > 0;
  const isInternal = user?.email?.endsWith(`@${env.VITE_INTERNAL_DOMAIN}`) ?? false;
  // Drive each section's collapsed CTA (open/continue the top app of that section).
  const firstReady = completedApps[0];
  const firstInProgress = incompleteApps[0];

  return (
    // min-h-full + my-auto centers the content when it fits, but top-aligns and stays
    // scrollable when the list overflows (plain justify-center clips the first card).
    <div className="flex min-h-full w-full flex-col">
      <div className="mx-auto my-auto w-full max-w-2xl px-4 pt-10 pb-24">
        <header className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-medium tracking-tight text-text-primary">Your applications</h1>
            <p className="mt-1.5 text-sm text-text-secondary">
              {hasCompleted
                ? "Open an existing application or continue a setup in progress."
                : "Choose an application to continue setup."}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isInternal && (
              <Link to="/admin">
                <Button variant="outline" className="gap-2">
                  <ShieldCheckIcon size={14} />
                  Admin
                </Button>
              </Link>
            )}
            <Link to="/onboarding" search={buildOnboardingSearch("add-app")}>
              <Button variant="outline" className="gap-2">
                <PlusIcon size={14} />
                Set up another application
              </Button>
            </Link>
          </div>
        </header>

        <div className="flex flex-col gap-6">
          {firstReady != null && (
            <AppSection
              title="Ready to use"
              count={completedApps.length}
              collapsedCta={
                <Link
                  to={firstReady.githubRepositoryId == null ? "/app/$appSlug/settings" : "/app/$appSlug"}
                  params={{ appSlug: firstReady.slug }}
                  aria-label={`Open ${firstReady.name}`}
                >
                  <Button variant="secondary" className="gap-2">
                    Open application
                    <ArrowRightIcon size={14} />
                  </Button>
                </Link>
              }
            >
              {completedApps.map((app) => (
                <CompletedAppCard key={app.id} app={app} />
              ))}
            </AppSection>
          )}

          {firstInProgress != null && (
            <AppSection
              title="Setup in progress"
              count={incompleteApps.length}
              collapsedCta={
                <Link
                  to="/onboarding"
                  search={buildResumeSearch(firstInProgress.onboardingState?.step, firstInProgress.id)}
                  aria-label={`Continue setting up ${firstInProgress.name}`}
                >
                  <Button className="gap-2">
                    Continue setup
                    <ArrowRightIcon size={14} />
                  </Button>
                </Link>
              }
            >
              {incompleteApps.map((app) => (
                <InProgressAppCard key={app.id} app={app} />
              ))}
            </AppSection>
          )}
        </div>

        <div className="mt-10 flex items-center justify-center gap-1.5 text-xs text-text-secondary">
          <QuestionIcon size={14} />
          <span>Need help?</span>
          <a
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-text-primary hover:underline"
          >
            Talk to support
          </a>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ title, count }: { title: string; count: number }) {
  return (
    <span className="flex items-center gap-2 text-sm font-medium text-text-primary">
      {title}
      <span className="grid min-w-5 place-items-center rounded-full bg-surface-raised px-1.5 py-0.5 text-2xs font-medium text-text-secondary tabular-nums">
        {count}
      </span>
    </span>
  );
}

function AppSection({
  title,
  count,
  collapsedCta,
  children,
}: {
  title: string;
  count: number;
  collapsedCta: ReactNode;
  children: ReactNode;
}) {
  const collapsible = count > COLLAPSE_THRESHOLD;
  const [open, setOpen] = useState(!collapsible);

  // Short lists render flat - no toggle, everything visible.
  if (!collapsible) {
    return (
      <section className="flex flex-col gap-3">
        <SectionHeading title={title} count={count} />
        <div className="flex flex-col gap-3">{children}</div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          className="flex items-center gap-2 text-left"
        >
          <SectionHeading title={title} count={count} />
          <CaretDownIcon size={16} className={cn("text-text-secondary transition-transform", open && "rotate-180")} />
        </button>
        {/* Collapsed: give a clear primary action instead of just a chevron. Expand to see them all. */}
        {!open && collapsedCta}
      </div>
      {open && <div className="flex flex-col gap-3">{children}</div>}
    </section>
  );
}

function AppGlyph({ name }: { name: string }) {
  return (
    <div className="grid size-10 shrink-0 place-items-center border border-border-mid bg-surface-raised font-mono text-sm font-medium text-text-primary">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function CompletedAppCard({ app }: { app: AppCardData }) {
  const hasNoRepo = app.githubRepositoryId == null;

  return (
    <Link
      to={hasNoRepo ? "/app/$appSlug/settings" : "/app/$appSlug"}
      params={{ appSlug: app.slug }}
      aria-label={hasNoRepo ? `Connect a repository for ${app.name}` : `Open ${app.name}`}
      className="group block"
    >
      <Card className="gap-4 p-4 transition-colors group-hover:border-primary group-focus-visible:border-primary">
        <div className="flex items-center gap-3">
          <AppGlyph name={app.name} />
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-text-primary">{app.name}</span>
            <span className="text-xs text-text-secondary">{appTypeLabel(app.architecture)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-xs text-text-secondary">
            {hasNoRepo ? (
              <>
                {/* Warn, not critical: an application waiting on a repository link is incomplete, not broken. */}
                <WarningCircleIcon size={14} weight="fill" className="text-status-warn" />
                No repository linked
              </>
            ) : (
              <>
                <CheckCircleIcon size={14} weight="fill" className="text-status-success" />
                Setup complete
              </>
            )}
          </span>
          {/* Named for where the card actually goes. Without a repository the link lands on settings, so
              offering to "open application" promised the dashboard and delivered a settings page. */}
          <span className={cn(buttonVariants({ variant: "secondary" }), "gap-2")}>
            {hasNoRepo ? "Connect a repository" : "Open application"}
            <ArrowRightIcon size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </Card>
    </Link>
  );
}

function InProgressAppCard({ app }: { app: AppCardData }) {
  const progress = getOnboardingProgress(app.onboardingState?.step);

  return (
    <Link
      to="/onboarding"
      search={buildResumeSearch(app.onboardingState?.step, app.id)}
      aria-label={`Continue setting up ${app.name}`}
      className="group block"
    >
      <Card className="gap-4 p-4 transition-colors group-hover:border-primary group-focus-visible:border-primary">
        <div className="flex items-center gap-3">
          <AppGlyph name={app.name} />
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-text-primary">{app.name}</span>
            <span className="text-xs text-text-secondary">{appTypeLabel(app.architecture)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-text-secondary tabular-nums">
            {progress.completed} of {progress.total} steps complete
          </span>
          <Progress value={progress.percent} />
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-text-secondary">
            Next: <span className="font-medium text-text-primary">{progress.nextStep}</span>
          </p>
          <span className={cn(buttonVariants({ variant: "default" }), "gap-2")}>
            Continue setup
            <ArrowRightIcon size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </Card>
    </Link>
  );
}
